import {useQuery} from 'react-query'
import {parseISO} from 'date-fns'

import {
  api,
  queryClient,
  useMutationQuery,
  replaceQueryClientDataItem,
} from 'base'
import {showSuccessNotification} from 'common/helpers'
import {FileFromServer} from 'common/types'
import {SecurityItem, DrawdownItem, ProjectFeedback, DrawdownDraftItem} from '.'

const syncDrawdownsAfterUpdate = (data: DrawdownItem, projectUuid: string) => {
  const drawdownRequestKey = ['drawdown', data.uuid]
  const drawdownsRequestKey = ['drawdowns', projectUuid]
  queryClient.setQueriesData(drawdownRequestKey, (oldData: DrawdownItem) => ({
    ...oldData,
    ...data,
  }))
  replaceQueryClientDataItem(drawdownsRequestKey, data)
  queryClient.invalidateQueries(drawdownRequestKey)
  queryClient.invalidateQueries(drawdownsRequestKey)
  queryClient.invalidateQueries(['project', projectUuid])
}

export const useGetProject = (uuid: string, enabled?: boolean) =>
  useQuery(
    ['project', uuid],
    async () => {
      const {data} = await api.get(`/project/${uuid}`)
      return data.data
    },
    {enabled}
  )

export const useGetProjectAml = (projectUuid: string) =>
  useQuery(['projectAml', projectUuid], async () => {
    const {data} = await api.get(`/amlCheck/list`, {
      params: {
        project: projectUuid,
        include: 'media',
      },
    })
    return data.data
  })

export const useApproveProjectAml = (
  amlUuid: string,
  type: string,
  projectUuid: string
) =>
  useMutationQuery(
    ['approveProjectAml', amlUuid, type],
    async () => {
      const {data} = await api.post(`/amlCheck/${amlUuid}/approve`)
      return data.data
    },
    {
      onSuccess(data) {
        const requestKey = ['projectAml', projectUuid]
        //TODO: because server returns all amls
        queryClient.setQueriesData(
          requestKey,
          data.filter((aml: any) => aml.uuid === amlUuid)
        )
        queryClient.invalidateQueries(requestKey)
        showSuccessNotification('AML has been verified.')
      },
    }
  )

export const useGetProjectConditions = (
  projectUuid: string,
  enabled?: boolean
) =>
  useQuery<any>(
    ['projectConditions', projectUuid],
    async () => {
      const {data} = await api.get('projectCondition/list', {
        params: {
          project: projectUuid,
        },
      })
      const sortedFilteredData: any[] = data.data.slice()
      sortedFilteredData.sort((a, b) => +parseISO(a.date) - +parseISO(b.date))
      return sortedFilteredData
    },
    {enabled}
  )

export const useAddProjectConditionDocument = (
  conditionUuid: string,
  projectUuid: string
) =>
  useMutationQuery<any, any, string>(
    ['addProjectConditionDocument', conditionUuid],
    async (documentUuid) => {
      const {data} = await api.post(
        `projectCondition/${conditionUuid}/documents/new`,
        {
          document: documentUuid,
          collection: 'documents',
        }
      )
      return data.data
    },
    {
      onSuccess(data) {
        const requestKey = ['projectConditions', projectUuid]
        replaceQueryClientDataItem(requestKey, data)
        queryClient.invalidateQueries(requestKey)
      },
    }
  )

export const useRemoveProjectConditionDocument = (
  conditionUuid: string,
  documentUuid: string,
  projectUuid: string
) =>
  useMutationQuery<any, any, string>(
    ['removeProjectConditionDocument', conditionUuid, documentUuid],
    async () => {
      const {data} = await api.delete(
        `/projectCondition/${conditionUuid}/documents/${documentUuid}`
      )
      return data.data
    },
    {
      onSuccess(data) {
        const requestKey = ['projectConditions', projectUuid]
        replaceQueryClientDataItem(requestKey, {
          ...data,
          documents: data.documents.filter(
            (document: FileFromServer) => document.uuid !== documentUuid
          ),
        })
        queryClient.invalidateQueries(requestKey)
      },
    }
  )

export const useCreateProjectCondition = (
  projectUuid: string,
  requestUuid: string,
  onClose: () => void
) =>
  useMutationQuery<any, any, any>(
    ['createProjectCondition', projectUuid],
    async (values) => {
      const {data} = await api.post('/projectCondition/new', {
        ...values,
        project: projectUuid,
      })
      return data.data
    },
    {
      onSuccess(data) {
        const requestKey = ['request', requestUuid]
        queryClient.setQueriesData(requestKey, (oldData: any) => {
          return {
            ...oldData,
            project: {
              ...oldData.project,
              conditions: [...oldData.project.conditions, data],
            },
          }
        })
        queryClient.invalidateQueries(requestKey)
        onClose()
      },
    }
  )

export const useCreateProjectConditions = (
  projectUuid: string,
  requestUuid: string,
  onSuccessActions: any
) =>
  useMutationQuery<any, any, any[]>(
    ['createProjectConditions', projectUuid],
    async (conditions) => {
      const responses = await Promise.all(
        conditions.map((condition) =>
          api.post('/projectCondition/new', {
            ...condition,
            project: projectUuid,
          })
        )
      )
      return responses
    },
    {
      onSuccess(responses) {
        const requestKey = ['request', requestUuid]
        responses.forEach(({data}: any) =>
          queryClient.setQueriesData(requestKey, (oldData: any) => {
            return {
              ...oldData,
              project: {
                ...oldData.project,
                conditions: [...oldData.project.conditions, data.data],
              },
            }
          })
        )
        queryClient.invalidateQueries(requestKey)
        onSuccessActions()
      },
    }
  )

export const useApproveProjectCondition = (conditionUuid: string) =>
  useMutationQuery(
    ['approveProjectCondition', conditionUuid],
    async () => {
      const {data} = await api.post(
        `/projectCondition/${conditionUuid}/approve`
      )
      return data.data
    },
    {
      onSuccess() {
        queryClient.invalidateQueries('request')
      },
    }
  )

export const useGetDrawdowns = (projectUuid: string) =>
  useQuery<DrawdownItem[]>(['drawdowns', projectUuid], async () => {
    const {data} = await api.get('/drawdown/list', {
      params: {
        project: projectUuid,
      },
    })
    const sortedData: any[] = data.data.slice()
    sortedData.sort((a, b) => b.sortOrder - a.sortOrder)
    return sortedData
  })

export const useGetDrawdown = (drawdownUuid: string) =>
  useQuery<DrawdownItem>(
    ['drawdown', drawdownUuid],
    async () => {
      const {data} = await api.get(`/drawdown/${drawdownUuid}`)
      return data.data
    },
    {enabled: Boolean(drawdownUuid)}
  )

export const useCreateDrawdown = (
  projectUuid: string,
  onSuccessActions: (drawdownUuid: string) => void
) =>
  useMutationQuery<DrawdownItem, any, any>(
    ['createDrawdown', projectUuid],
    async (values) => {
      const {data} = await api.post('/drawdown/new', {
        ...values,
        project: projectUuid,
      })
      return data.data
    },
    {
      onSuccess(newDrawdown) {
        const drawdownsRequestKey = ['drawdowns', projectUuid]
        queryClient.setQueriesData(
          drawdownsRequestKey,
          (oldDrawdowns: DrawdownItem[]) => [newDrawdown, ...oldDrawdowns]
        )
        queryClient.invalidateQueries(drawdownsRequestKey)
        queryClient.invalidateQueries(['project', projectUuid])
        onSuccessActions(newDrawdown.uuid)
      },
    }
  )

export const useCreateDrawdownDraft = (
  onSuccessActions: () => void
) => useMutationQuery<DrawdownItem>(
  ['createDrawdownDraft'],
  async (values) => {
    const {data} = await api.post('/drawdown/create/draft', values)
    return data.data
  },
  {
    onSuccess() {
      onSuccessActions();
    }
  }
)

export const useGetDrawdownDraft = (
  projectUuid: string
) => useMutationQuery<DrawdownDraftItem>(
  ['getDrawdownDraft'],
  async () => {
    const {data} = await api.get(`/drawdown/project/${projectUuid}/draft`)
    return data.data;
  }
)

export const useUpdateDrawdown = (
  projectUuid: string,
  drawdownUuid: string,
  onSuccessAction?: () => void
) =>
  useMutationQuery<DrawdownItem, any, any>(
    ['updateDrawdown', drawdownUuid],
    async (values) => {
      const {data} = await api.post(`/drawdown/${drawdownUuid}`, values)
      return data.data
    },
    {
      onSuccess(data) {
        syncDrawdownsAfterUpdate(data, projectUuid)
        queryClient.invalidateQueries(['forecast', projectUuid])
        onSuccessAction && onSuccessAction()
      },
    }
  )

export const useAttachDrawdownDocument = (
  projectUuid: string,
  drawdownUuid: string
) =>
  useMutationQuery<any, any, string>(
    ['attachDrawdownDocument', drawdownUuid],
    async (fileUuid) => {
      const {data} = await api.post(`/drawdown/${drawdownUuid}/documents/new`, {
        document: fileUuid,
      })
      return data.data
    },
    {
      onSuccess(data) {
        syncDrawdownsAfterUpdate(data, projectUuid)
      },
    }
  )

export const useRemoveDrawdownDocument = (
  projectUuid: string,
  drawdownUuid: string,
  documentUuid: string
) =>
  useMutationQuery<any, any, string>(
    ['removeDrawdownDocument', documentUuid],
    async () => {
      const {data} = await api.delete(
        `/drawdown/${drawdownUuid}/documents/${documentUuid}`
      )
      return data.data
    },
    {
      onSuccess(data) {
        syncDrawdownsAfterUpdate(data, projectUuid)
      },
    }
  )

export const useAcceptDrawdownDocuments = (
  projectUuid: string,
  drawdownUuid: string
) =>
  useMutationQuery<DrawdownItem>(
    ['acceptDrawdownDocuments', drawdownUuid],
    async () => {
      const {data} = await api.post(
        `/drawdown/${drawdownUuid}/documents/accept`
      )
      return data.data
    },
    {
      onSuccess(data) {
        syncDrawdownsAfterUpdate(data, projectUuid)
        showSuccessNotification('Drawdown documents has been accepted.')
      },
    }
  )

export const useApproveDrawdown = (projectUuid: string, drawdownUuid: string) =>
  useMutationQuery<DrawdownItem>(
    ['approveDrawdown', drawdownUuid],
    async () => {
      const {data} = await api.post(`/drawdown/${drawdownUuid}/approve`);
      return data.data
    },
    {
      onSuccess(data) {
        syncDrawdownsAfterUpdate(data, projectUuid);
        queryClient.invalidateQueries(['forecast', projectUuid]);
        showSuccessNotification('Drawdown has been approved.');
      },
    }
  )

export const useDeclineDrawdown = (projectUuid: string, drawdownUuid: string) =>
  useMutationQuery<DrawdownItem>(
    ['declineDrawdown', drawdownUuid],
    async () => {
      const {data} = await api.post(`/drawdown/${drawdownUuid}/decline`)
      return data.data
    },
    {
      onSuccess(data) {
        syncDrawdownsAfterUpdate(data, projectUuid)
        showSuccessNotification('Drawdown has been declined.')
      },
    }
  )

/**
 * recall drawdown
 * @param projectUuid 
 * @param drawdownUuid 
 * @returns 
 */
export const useRecallDrawdown = (projectUuid: string, drawdownUuid: string, isUpdateForcast: boolean = false) =>
  useMutationQuery<DrawdownItem>(
    ['recallDrawdown', drawdownUuid],
    async () => {
      const {data} = await api.post(`/drawdown/${drawdownUuid}/recall`)
      return data.data
    },
    {
      onSuccess(data) {
        syncDrawdownsAfterUpdate(data, projectUuid);
        isUpdateForcast && queryClient.invalidateQueries(['forecast', projectUuid]);
        showSuccessNotification('Drawdown has been recalled.');
      },
    }
  )

  /**
   * push back drawdown (FC only)
   * @param projectUuid 
   * @param drawdownUuid 
   * @param onSuccessActions 
   * @returns 
   */
export const usePushbackDrawdown = (
  projectUuid: string,
  drawdownUuid: string,
  onSuccessActions: () => void
) =>
  useMutationQuery<DrawdownItem>(
    ['pushbackDrawdown', drawdownUuid],
    async (reason) => {
      const {data} = await api.post(`/drawdown/${drawdownUuid}/pushBack`, reason);
      return data.data;
    },
    {
      onSuccess(data) {
        syncDrawdownsAfterUpdate(data, projectUuid);
        showSuccessNotification('Drawdown has been pushed back.');
        onSuccessActions();
      },
    }
  )
  
export const useSendDrawdownCode = (
  drawdownUuid: string,
  onSuccessAction?: () => void
) =>
  useMutationQuery<any>(
    ['sendDrawdownCode', drawdownUuid],
    async () => {
      const {data} = await api.post(`/drawdown/${drawdownUuid}/confirm/send`)
      return data
    },
    {
      onSuccess() {
        onSuccessAction?.()
      },
    }
  )

export const useConfirmDrawdownCode = (
  projectUuid: string,
  drawdownUuid: string,
  onClose: () => void
) =>
  useMutationQuery<DrawdownItem>(
    ['confirmDrawdownCode', drawdownUuid],
    async (code) => {
      const {data} = await api.post(`/drawdown/${drawdownUuid}/confirm/${code}`)
      return data.data
    },
    {
      onSuccess(data) {
        syncDrawdownsAfterUpdate(data, projectUuid)
        showSuccessNotification('Drawdown has been confirmed.')
        onClose()
      },
    }
  )

export const useCreateProjectReview = (
  projectUuid: string,
  onClose: () => void
) =>
  useMutationQuery<any, any, any>(
    ['createProjectReview', projectUuid],
    async (values) => {
      const {data} = await api.post('/projectManagerReview/new', {
        ...values,
        project: projectUuid,
      })
      return data.data
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(['project', projectUuid])
        onClose()
      },
    }
  )

export const useGetProjectSchedule = (uuid: string, isVariationRequest: boolean) => {
  return useQuery<any>(
    ['projectSchedule', uuid],
    async () => {
      const {data} = !isVariationRequest
        ? await api.get(`/project/${uuid}/schedule`)
        : await api.get(`/variationRequest/${uuid}/schedule`)
      ;
      return data.data
    }
  )
}

export const useGetProjectFeedback = (projectUuid: string) =>
  useQuery<ProjectFeedback[]>(['projectFeedback', projectUuid], async () => {
    const {data} = await api.get('/projectFeedback/list', {
      params: {
        project: projectUuid,
      },
    })
    return data.data
  })

export const useCreateProjectFeedback = (
  projectUuid: string,
  onClose: () => void
) =>
  useMutationQuery<ProjectFeedback, any, any>(
    ['createProjectFeedback', projectUuid],
    async (values) => {
      const {data} = await api.post('/projectFeedback/new', {
        ...values,
        project: projectUuid,
      })
      return data.data
    },
    {
      onSuccess(data) {
        const requestKey = ['projectFeedback', projectUuid]
        queryClient.setQueriesData(requestKey, (oldData: ProjectFeedback[]) => [
          data,
          ...oldData,
        ])
        queryClient.invalidateQueries(requestKey)
        onClose()
      },
    }
  )

export const useAttachLoanSummaryFile = (projectUuid: string) =>
  useMutationQuery<any, any, string>(
    ['attachLoanSummaryFile', projectUuid],
    async (fileUuid) => {
      const {data} = await api.post(
        `/project/${projectUuid}/documents/loanSummary`,
        {
          document: fileUuid,
        }
      )
      return data
    },
    {
      onSuccess() {
        queryClient.invalidateQueries('request')
        queryClient.invalidateQueries('project')
        showSuccessNotification('Loan Summary file has been updated.')
      },
    }
  )

export const useAttachLendingOfferFile = (projectUuid: string) =>
  useMutationQuery<any, any, string>(
    ['attachLendingOfferFile', projectUuid],
    async (fileUuid) => {
      const {data} = await api.post(
        `/project/${projectUuid}/documents/lendingOffer`,
        {
          document: fileUuid,
        }
      )
      return data
    },
    {
      onSuccess() {
        queryClient.invalidateQueries('request')
        queryClient.invalidateQueries('project')
        showSuccessNotification('Lending Offer file has been updated.')
      },
    }
  )

export const useGetProjectTransactions = (projectUuid: string) =>
  useQuery<any>(['projectTransactions', projectUuid], async () => {
    const {data} = await api.get('/transaction/list', {
      params: {
        project: projectUuid,
      },
    })
    return data.data
  })

export const useArchiveProject = (projectUuid: string) =>
  useMutationQuery(
    ['archiveProject', projectUuid],
    async () => {
      const {data} = await api.post(`/project/${projectUuid}/archive`)
      return data.data
    },
    {
      onSuccess(newProject) {
        const requestKey = ['project', projectUuid]
        queryClient.setQueriesData(requestKey, newProject)
        queryClient.invalidateQueries(requestKey)
      },
    }
  )

export const useGetSecurityItems = (projectUuid: string, enabled:boolean) =>
  useQuery<SecurityItem[]>(['securityItems', projectUuid], async () => {
    const {data} = await api.get('/securityItem/list', {
      params: {
        project: projectUuid,
        include: ['statuses'],
      },
    })
    const sortedData: SecurityItem[] = data.data.slice()
    sortedData.sort((a, b) => +parseISO(a.createdAt) - +parseISO(b.createdAt))
    return sortedData
  }, {enabled})

export const useCreateSecurityItem = (
  projectUuid: string,
  onClose: () => void
) =>
  useMutationQuery<SecurityItem, any, any>(
    ['createSecurityItem', projectUuid],
    async (values) => {
      const {data} = await api.post('/securityItem/new', {
        ...values,
        project: projectUuid,
      })
      return data.data
    },
    {
      onSuccess(newItem) {
        const requestKey = ['securityItems', projectUuid]
        queryClient.setQueriesData(requestKey, (oldData: SecurityItem[]) => [
          ...oldData,
          newItem,
        ])
        queryClient.invalidateQueries(requestKey)
        queryClient.invalidateQueries(['project', projectUuid])
        onClose()
      },
    }
  )

export const useUpdateSecurityItem = (
  projectUuid: string,
  itemUuid: string,
  onClose: () => void
) =>
  useMutationQuery<SecurityItem, any, any>(
    ['updateSecurityItem', itemUuid],
    async (values) => {
      const {data} = await api.post(`/securityItem/${itemUuid}`, values)
      return data.data
    },
    {
      onSuccess(newItem) {
        const requestKey = ['securityItems', projectUuid]
        replaceQueryClientDataItem(requestKey, newItem)
        queryClient.invalidateQueries(requestKey)
        queryClient.invalidateQueries(['project', projectUuid])
        onClose()
      },
    }
  )

export const useDischargeSecurityItem = (
  projectUuid: string,
  itemUuid: string,
  onClose: () => void
) =>
  useMutationQuery(
    ['dischargeSecurityItem', itemUuid],
    async () => {
      const {data} = await api.post(
        `/securityItem/${itemUuid}/discharge/toggle`
      )
      return data.data
    },
    {
      onSuccess(newItem) {
        const requestKey = ['securityItems', projectUuid]
        replaceQueryClientDataItem(requestKey, newItem)
        queryClient.invalidateQueries(requestKey)
        queryClient.invalidateQueries(['project', projectUuid])
        onClose()
      },
    }
  )

export const useSyncProjectXero = (projectUuid: string) =>
  useMutationQuery(
    ['syncProjectXero', projectUuid],
    async () => {
      const {data} = await api.post(`/project/${projectUuid}/sync`)
      return data.data
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(['project', projectUuid])
        showSuccessNotification(
          'Project transactions has been synced with Xero.'
        )
      },
    }
  )
